import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Link as MuiLink,
  Skeleton,
  IconButton,
  InputAdornment,
  Autocomplete,
  Tabs,
  Tab,
  Checkbox,
  Grid2,
} from '@mui/material';
import { API_URL, BASE_URL } from '../Config';
import config from "../common/Config";
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Iconify } from "src/components/iconify";
import '../common/commonStyles.css';
import { toast } from 'sonner';
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { fetchGetPicker } from "../../utils/pickingApi";

const EditPicking = () => {
  const { picklistNo } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [pickerOptions, setPickerOptions] = useState([]);
  const [selectedPicker, setSelectedPicker] = useState(null);
  const [newOrders, setNewOrders] = useState([]);
  const [addNewOrders, setAddNewOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAddNew, setLoadingAddNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermAddNew, setSearchTermAddNew] = useState('');
  const [page, setPage] = useState(0);
  const [pageAddNew, setPageAddNew] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [rowsPerPageAddNew, setRowsPerPageAddNew] = useState(50);
  const [checkedValues, setCheckedValues] = useState([]);
  const accessToken = localStorage.getItem('accessToken');
  const isSinglePicking = localStorage.getItem('isSinglePicking');

  const editPickingColumns = [
    { id: "date", label: "Order Date", width: 120 },
    { id: "customer", label: "Customers (Ac/No)", width: 200 },
    { id: "reference_no", label: "Order Id", width: 120 },
    { id: "grand_total", label: "Invoice Amount", width: 150, numeric: true },
    { id: "sales_rep_collected", label: "Sales Rep Collected", width: 150, numeric: true },
    { id: "route_number", label: "Route No", width: 120 },
    { id: "postal_code", label: "Postal Code", width: 120 },
    { id: "action", label: "Action", width: 100 },
  ];

  const addNewOrderColumns = [
    { id: "checkbox", label: "#", width: 70 },
    { id: "date", label: "Order Date", width: 100 },
    { id: "customer", label: "Customers (Ac/No)", width: 280 },
    { id: "reference_no", label: "Order Id", width: 80 },
    { id: "grand_total", label: "Grand Total", width: 150, numeric: true },
    { id: "route_number", label: "Route No", width: 120 },
    { id: "postal_code", label: "Postal Code", width: 120 },
    { id: "first_name", label: "Sales Person", width: 150 },
    { id: "deliverydate", label: "Delivery Date", width: 150 },
  ];

  useEffect(() => {
    async function fetchData() {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        window.location.href = `${config.BASE_URL}/login`;
        return;
      }

      setLoading(true);
      try {
        const settings = await getCompanySettings();
        const useEncrypt = resolveCustomerApiEncrypt(settings);
        const headers = { authorization: `Bearer ${accessToken}` };
        let body;
        if (useEncrypt) {
          const ep = encryptPayload({ picklist_number: picklistNo });
          if (!ep) {
            setNewOrders([]);
            return;
          }
          const fd = new FormData();
          fd.append("encrypted_payload", ep);
          body = fd;
        } else {
          const formData = new FormData();
          formData.append('picklist_number', picklistNo);
          body = formData;
        }

        const response = await fetch(`${API_URL}/edit_picking_order_list`, {
          method: 'POST',
          headers,
          body,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch orders');
        }

        const json = await response.json();
        const data = normalizeEncryptedApiResponse(json, useEncrypt) ?? json;
        setNewOrders(data.data || []);
        if (data.data && data.data.length > 0 && data.data[0].picker_id) {
          const picker = pickerOptions.find(p => p.value === data.data[0].picker_id);
          if (picker) {
            setSelectedPicker(picker);
          }
        }
      } catch {
      } finally {
        setLoading(false);
      }
    }

    if (tabValue === 0) {
      fetchData();
    }
  }, [picklistNo, tabValue, pickerOptions]);

  useEffect(() => {
    async function fetchAddNewOrders() {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        window.location.href = `${config.BASE_URL}/login`;
        return;
      }

      setLoadingAddNew(true);
      try {
        const settings = await getCompanySettings();
        const useEncrypt = resolveCustomerApiEncrypt(settings);
        const headers = { authorization: `Bearer ${accessToken}` };
        let body;
        if (useEncrypt) {
          const plain = {
            draw: "1",
            start: "0",
            length: "10000",
            searchValue: searchTermAddNew,
            picklist_number: picklistNo,
          };
          const ep = encryptPayload(plain);
          if (!ep) {
            setAddNewOrders([]);
            return;
          }
          const fd = new FormData();
          fd.append("encrypted_payload", ep);
          body = fd;
        } else {
          const formData = new FormData();
          formData.append('draw', 1);
          formData.append('start', 0);
          formData.append('length', 10000);
          formData.append('searchValue', searchTermAddNew);
          formData.append('picklist_number', picklistNo);
          body = formData;
        }

        const response = await fetch(`${API_URL}/picking/add_order_list`, {
          method: 'POST',
          headers,
          body,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch orders');
        }

        const json = await response.json();
        const data = normalizeEncryptedApiResponse(json, useEncrypt) ?? json;
        if (data && data.data) {
          const orders = Array.isArray(data.data) ? data.data : [];
          setAddNewOrders(orders);
        } else if (Array.isArray(data)) {
          setAddNewOrders(data);
        } else {
          setAddNewOrders([]);
        }
      } catch {
        setAddNewOrders([]);
      } finally {
        setLoadingAddNew(false);
      }
    }

    if (tabValue === 1) {
      fetchAddNewOrders();
    }
  }, [tabValue, searchTermAddNew, picklistNo]);

  useEffect(() => {
    async function fetchPickerOptions() {
      const accessToken = localStorage.getItem('accessToken');
      const token = localStorage.getItem('token');
      if (!accessToken) {
        window.location.href = `${config.BASE_URL}/login`;
        return;
      }

      try {
        const payload = await fetchGetPicker(token);

        if (!payload || !payload.picker || !Array.isArray(payload.picker)) {
          throw new Error('Invalid response format or empty picker array');
        }

        const formattedOptions = payload.picker.map(item => ({
          label: `${item.first_name} ${item.last_name}`,
          value: item.id,
        }));

        setPickerOptions(formattedOptions);
      } catch {
      }
    }

    fetchPickerOptions();
  }, []);

  const handleButtonClick = async (orderId, picklistNumber) => {
    const formData = new FormData();
    formData.append('orderId', orderId);
    formData.append('picklistNumber', picklistNumber);

    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    try {
      const response = await fetch(`${API_URL}/remove_order_picklist`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      if (data.status) {
        setNewOrders((prevOrders) => prevOrders.filter(order => order.action !== orderId));
        toast.success('Order removed successfully!');
      } else {
        toast.error('Failed to remove the order.');
      }
    } catch (error) {
      console.error('API call error:', error);
      toast.error('An error occurred while removing the order.');
    }
  };

  const handleSelectionSubmit = async (e) => {
    e.preventDefault();

    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    if (!selectedPicker || !selectedPicker.value) {
      toast.error('Please select a picker.');
      return;
    }

    const formData = new URLSearchParams();
    formData.append('picker_id', selectedPicker.value);
    formData.append('selectedOrderIds', JSON.stringify([]));
    formData.append('picklist_number', picklistNo);

    try {
      const response = await fetch(`${API_URL}/update_picker`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (!response.ok) {
        throw new Error('Failed to submit selection');
      }

      await response.json();
      toast.success('Picker Update successfully');
    } catch (error) {
      console.error('Error submitting selection:', error);
      toast.error('Failed to update picker.');
    }
  };

  const handleSubmit = async () => {
    if (checkedValues.length > 0) {
      try {
        const settings = await getCompanySettings();
        const useEncrypt = resolveCustomerApiEncrypt(settings);
        const headers = { authorization: `Bearer ${accessToken}` };
        let body;
        if (useEncrypt) {
          const plain = {
            picklist_number: picklistNo,
            pickerid: String(selectedPicker?.value || ''),
            val: checkedValues.map(String),
          };
          const ep = encryptPayload(plain);
          if (!ep) {
            toast.error('Something went wrong, try again');
            return;
          }
          const fd = new FormData();
          fd.append("encrypted_payload", ep);
          body = fd;
        } else {
          const formData = new FormData();
          formData.append('picklist_number', picklistNo);
          formData.append('pickerid', selectedPicker?.value || '');
          checkedValues.forEach((value) => {
            formData.append('val[]', value);
          });
          body = formData;
        }

        const response = await fetch(`${config.API_URL}/add_order_picking_list`, {
          method: 'POST',
          headers,
          body,
        });

        if (!response.ok) {
          toast.error('Something went wrong, try again');
          return;
        }

        const json = await response.json();
        const data = normalizeEncryptedApiResponse(json, useEncrypt) ?? json;
        if (data.status) {
          setCheckedValues([]);
          toast.success('Picking Update Successfully');
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          toast.error('Failed to add the Picking.');
        }
      } catch {
        toast.error('Error Picking Order.');
      }
    } else {
      toast.error('Please select order to picking.');
    }
  };

  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) {
      return newOrders;
    }
    const searchLower = searchTerm.toLowerCase().trim();
    return newOrders.filter((order) => {
      return (
        (order.date && order.date.toLowerCase().includes(searchLower)) ||
        (order.customer && order.customer.toLowerCase().includes(searchLower)) ||
        (order.reference_no && String(order.reference_no).toLowerCase().includes(searchLower)) ||
        (order.route_number && String(order.route_number).toLowerCase().includes(searchLower)) ||
        (order.postal_code && order.postal_code.toLowerCase().includes(searchLower))
      );
    });
  }, [newOrders, searchTerm]);

  const filteredAddNewOrders = useMemo(() => {
    if (!searchTermAddNew.trim()) {
      return addNewOrders;
    }
    const searchLower = searchTermAddNew.toLowerCase().trim();
    return addNewOrders.filter((order) => {
      return (
        (order.date && order.date.toLowerCase().includes(searchLower)) ||
        (order.customer && order.customer.toLowerCase().includes(searchLower)) ||
        (order.reference_no && String(order.reference_no).toLowerCase().includes(searchLower)) ||
        (order.route_number && String(order.route_number).toLowerCase().includes(searchLower)) ||
        (order.postal_code && order.postal_code.toLowerCase().includes(searchLower)) ||
        (order.first_name && order.first_name.toLowerCase().includes(searchLower)) ||
        (order.deliverydate && order.deliverydate.toLowerCase().includes(searchLower))
      );
    });
  }, [addNewOrders, searchTermAddNew]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  useEffect(() => {
    setPageAddNew(0);
  }, [searchTermAddNew]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePageAddNew = (event, newPage) => {
    setPageAddNew(newPage);
  };

  const handleChangeRowsPerPageAddNew = (event) => {
    setRowsPerPageAddNew(parseInt(event.target.value, 10));
    setPageAddNew(0);
  };

  const paginatedOrders = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredOrders.slice(startIndex, endIndex);
  }, [filteredOrders, page, rowsPerPage]);

  const paginatedAddNewOrders = useMemo(() => {
    const startIndex = pageAddNew * rowsPerPageAddNew;
    const endIndex = startIndex + rowsPerPageAddNew;
    return filteredAddNewOrders.slice(startIndex, endIndex);
  }, [filteredAddNewOrders, pageAddNew, rowsPerPageAddNew]);

  const handleCheckboxChange = (orderId) => {
    setCheckedValues((prev) => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = paginatedAddNewOrders.map(order => order.id);
      setCheckedValues((prev) => [...new Set([...prev, ...allIds])]);
    } else {
      const pageIds = paginatedAddNewOrders.map(order => order.id);
      setCheckedValues((prev) => prev.filter(id => !pageIds.includes(id)));
    }
  };

  const allSelectedOnPage = paginatedAddNewOrders.length > 0 && paginatedAddNewOrders.every((order) => checkedValues.includes(order.id));

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1 }} >
            <Grid2 container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
              <Grid2 size={{ xs: 12, md: 2 }}>
                <Typography className="page-title">
                  Edit Picking
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 9 }}>
                <Tabs
                  value={tabValue}
                  onChange={(e, newValue) => setTabValue(newValue)} className="common-tabs">
                  <Tab label="Edit Picking"
                    className="common-tab" />

                  {isSinglePicking === 'N' && <Tab label="Add New Order"
                    className="common-tab" />}
                </Tabs>
              </Grid2>
            </Grid2>
          </Box>

          {tabValue === 0 && (
            <>
              <Grid2 container spacing={1} sx={{ px: 2, pb: 1 }} display="flex" justifyContent="space-between" alignItems="center">
                <Grid2 size={{ xs: 12, md: 4 }} >
                  <Typography sx={{ fontWeight: 500, color: "#212b36", fontSize: "1rem" }}>
                    Picklist No:  {picklistNo}
                  </Typography >
                </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: { xs: "stretch", md: "flex-end" }, gap: { xs: 1.5, sm: 2 } }}>
                    <TextField
                      className="input-border"
                      fullWidth
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="small"
                      placeholder="Search orders..."
               
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Iconify icon="eva:search-outline"/>
                            </InputAdornment>
                          )
                        }
                      }}
            
                    />
                  </Box>
                </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <form onSubmit={handleSelectionSubmit} style={{ width: '100%' }}>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1.5, sm: 2 }, width: "100%" }}>
                      <Autocomplete
                        options={pickerOptions}
                        value={selectedPicker}
                        onChange={(event, newValue) => setSelectedPicker(newValue)}
                        getOptionLabel={(option) => option.label || ''}
                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                        sx={{ width: { xs: "100%", sm: "350px" } }}
                        renderInput={(params) => (
                          <TextField
                            className="input-border"
                            {...params}
                            placeholder="Re-assigned picker"
                            size="small"
                 
                          />
                        )}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        className="button-variant-contained"
                        sx={{
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        Submit
                      </Button>
                    </Box>
                  </form>
                </Grid2>
              </Grid2>

              <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                <Table
                  size="small"
                  className="table-cell-border"
                >
                  <colgroup>
                    {editPickingColumns.map((column) => (
                      <col key={column.id} style={{ width: column.width ? `${column.width}px` : "auto" }} />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow className="table-cell-header">
                      {editPickingColumns.map((column) => (
                        <TableCell
                          className="table-cell-header"
                          key={column.id}
                          align={column.numeric ? "right" : "left"}
                          sx={{
                            width: { xs: "auto", sm: column.width ? `${column.width}px` : "auto" },
                            minWidth: { xs: column.width ? `${Math.min(column.width, 100)}px` : "auto", sm: column.width ? `${column.width}px` : "auto" }
                          }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 10 }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          {editPickingColumns.map((column) => (
                            <TableCell
                              key={column.id}
                              align={column.numeric ? "right" : "left"}
                              sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, width: { xs: "auto", sm: column.width ? `${column.width}px` : "auto" } }}
                            >
                              <Skeleton variant="text" width="60%" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : paginatedOrders.length > 0 ? (
                      paginatedOrders.map((row) => (
                        <TableRow
                          key={row.action}
                          hover
                          className="table-cell-style"

                        >
                          <TableCell >
                            {row.date}
                          </TableCell>
                          <TableCell sx={{ wordBreak: 'break-word' }}>
                            {row.customer}
                          </TableCell>
                          <TableCell >
                            <MuiLink
                              href={`${BASE_URL}/trip_summary/print/${row.action}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: "inherit",
                                textDecoration: "underline",
                                textDecorationColor: "currentColor",
                                "&:hover": {
                                  textDecoration: "underline",
                                  textDecorationColor: "currentColor"
                                }
                              }}
                            >
                              {row.reference_no}
                            </MuiLink>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {row.grand_total}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {row.sales_rep_collected}
                          </TableCell>
                          <TableCell align="center" >
                            {row.route_number}
                          </TableCell>
                          <TableCell align="center" >
                            {row.postal_code}
                          </TableCell>
                          <TableCell align="center" >
                            <IconButton
                              size="small"
                              onClick={() => handleButtonClick(row.action, picklistNo)}
                              sx={{ color: "error.main" }}
                              title="Delete"
                            >
                              <Iconify icon="eva:trash-2-fill" width={18} height={18} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={editPickingColumns.length} sx={{ py: 8 }}>
                          <Typography align="center">
                            No orders found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <TablePagination
                  component="div"
                  count={filteredOrders.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  className="pagination-styles"
                />
              </Box>
            </>
          )}

          {tabValue === 1 && (
            <>
              <Grid2 container spacing={{ xs: 1.5, sm: 2 }} alignItems="center" sx={{ px: 2, pb: 1 }}>
                <Grid2 size={{ xs: 12, md: 3 }} >
                  <Typography sx={{ fontWeight: 500, color: "#212b36", fontSize: "1rem" }}>
                    Picklist No: {picklistNo}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 9 }} >
                  <Box sx={{ display: "flex", justifyContent: { xs: "stretch", md: "flex-end" } }}>
                    <TextField
                      className="input-border"
                      fullWidth
                      value={searchTermAddNew}
                      onChange={(e) => setSearchTermAddNew(e.target.value)}
                      size="small"
                      placeholder="Search orders..."
                      variant="outlined"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Iconify icon="eva:search-outline"/>
                            </InputAdornment>
                          )
                        }
                      }}
                      sx={{
                        width: { xs: "100%", md: "400px" },
                      
                      }}
                    />
                  </Box>
                </Grid2>
              </Grid2>

              <TableContainer>
                <Table
                  size="small"
                  className="table-cell-border"
                >
                  <colgroup>
                    {addNewOrderColumns.map((column) => (
                      <col key={column.id} style={{ width: column.width ? `${column.width}px` : "auto" }} />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow className="table-cell-header">
                      {addNewOrderColumns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.id === "checkbox" ? "center" : column.numeric ? "right" : "left"}
                          sx={{
                            width: { xs: "auto", sm: column.width ? `${column.width}px` : "auto" },
                            minWidth: { xs: column.width ? `${Math.min(column.width, 100)}px` : "auto", sm: column.width ? `${column.width}px` : "auto" }
                          }}
                        >
                          {column.id === "checkbox" ? (
                            <Checkbox
                              indeterminate={paginatedAddNewOrders.some((order) => checkedValues.includes(order.id)) && !allSelectedOnPage}
                              checked={allSelectedOnPage && paginatedAddNewOrders.length > 0}
                              onChange={handleSelectAll}
                              size="small"
                              className="checkBox"
                            />
                          ) : (
                            column.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingAddNew ? (
                      Array.from({ length: 10 }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          {addNewOrderColumns.map((column) => (
                            <TableCell
                              key={column.id}
                              align={column.id === "checkbox" ? "center" : column.numeric ? "right" : "left"}
                              sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, width: { xs: "auto", sm: column.width ? `${column.width}px` : "auto" } }}
                            >
                              {column.id === "checkbox" ? (
                                <Skeleton variant="rectangular" width={36} height={20} />
                              ) : (
                                <Skeleton variant="text" width="60%" />
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : paginatedAddNewOrders.length > 0 ? (
                      paginatedAddNewOrders.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          className="table-cell-style"
                        >
                          <TableCell padding="checkbox" align="center">
                            <Checkbox
                              checked={checkedValues.includes(row.id)}
                              onChange={() => handleCheckboxChange(row.id)}
                              size="small"
                              className="checkBox"
                            />
                          </TableCell>
                          <TableCell >
                            {row.date}
                          </TableCell>
                          <TableCell sx={{ wordBreak: 'break-word' }}>
                            {row.customer}
                          </TableCell>
                          <TableCell>
                            {row.reference_no}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {row.grand_total}
                          </TableCell>
                          <TableCell align="center">
                            {row.route_number}
                          </TableCell>
                          <TableCell align="center">
                            {row.postal_code}
                          </TableCell>
                          <TableCell >
                            {row.first_name}
                          </TableCell>
                          <TableCell >
                            {row.deliverydate}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={addNewOrderColumns.length} sx={{ py: 8 }}>
                          <Typography align="center">
                            No orders found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", sm: "center" }, gap: { xs: 1.5, sm: 0 } }}>
                <Button
                  variant="contained"
                  className="button-variant-contained"
                  onClick={handleSubmit}
                  sx={{
                    mx: 1,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Submit
                </Button>
                <TablePagination
                  component="div"
                  count={filteredAddNewOrders.length}
                  page={pageAddNew}
                  onPageChange={handleChangePageAddNew}
                  rowsPerPage={rowsPerPageAddNew}
                  onRowsPerPageChange={handleChangeRowsPerPageAddNew}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  className="pagination-styles"
                />
              </Box>
            </>
          )}
        </Paper >
      </Box >
    </>
  );
};

export default EditPicking;
